import { useLocation, useNavigate } from 'react-router-dom';
import { useStepper } from '@fiverr-private/navigation';
import { STEP_SLUGS } from '@fiverr-private/store_builder_types';
import { calculateStepperData } from '../../utils/calculateStepperData';
import { getCurrentStep, getPreviousStep, getNextStep } from '../../utils/stepNavigation';
import { STEP_GROUPS } from '../../../lib/constants/steps';
import { SHOPIFY } from '../../../lib/constants/routes';
import { isLastStepInStepGroups } from '../../utils/isLastStepInStepGroups';
import { getStepGroup } from '../../../lib/utils/stepCalculator/stepUtils';
import { useInitialProps } from '../useInitialProps';
import { useStoreData } from '../useStoreData';

export const useWizardNavigation = () => {
    const { currentStep: initialStepNumber } = useInitialProps();
    const { storeData } = useStoreData();
    const steps = calculateStepperData(initialStepNumber);
    const stepperResult = useStepper({
        steps,
        stepStrategy: 'linear',
        orientation: 'horizontal',
    });

    const navigate = useNavigate();
    const location = useLocation();

    const currentPath = getCurrentStep(location.pathname);
    const nextStepPath = getNextStep(currentPath, storeData);
    const previousStepPath = getPreviousStep(currentPath, storeData);

    const currentStepIndex = STEP_SLUGS.indexOf(currentPath);

    const hasNextStep = currentStepIndex < STEP_SLUGS.length - 1;
    const hasPrevStep = currentStepIndex > 0;

    const {
        setStepCompleteState,
        nextStep: nextStepperCallback,
        currentStep: currentStepperIndex,
        prevStep: prevStepperCallback,
    } = stepperResult;

    const navigateToNextStep = () => {
        const isLastStep = isLastStepInStepGroups(STEP_GROUPS[currentStepperIndex], currentPath);
        const nextStepGroup = nextStepPath ? getStepGroup(nextStepPath) : null;
        const isGroupTransition = nextStepGroup && STEP_GROUPS.indexOf(nextStepGroup) > currentStepperIndex;

        if (isLastStep || isGroupTransition) {
            setStepCompleteState(true, currentStepperIndex);
            nextStepperCallback();
        }
        navigate(`${SHOPIFY}/${nextStepPath}`);
    };

    const navigateToPreviousStep = () => {
        const previousStepIndex = currentStepIndex - 1;
        const previousStepPath = STEP_SLUGS[previousStepIndex];
        const currentGroup = getStepGroup(currentPath);
        const previousGroup = getStepGroup(previousStepPath);

        if (currentGroup !== previousGroup) {
            setStepCompleteState(false, STEP_GROUPS.indexOf(currentGroup));
            prevStepperCallback();
        }

        navigate(`${SHOPIFY}/${previousStepPath}`);
    };

    return {
        stepperResult,
        nextStepPath,
        previousStepPath,
        currentPath,
        hasPrevStep,
        hasNextStep,
        navigateToNextStep,
        navigateToPreviousStep,
    };
};
